import { localeLabel } from "@/lib/locale";
import type { Locale } from "@/lib/locale";
// Preferred client types a professional serves — a RANKED subset of a fixed
// four-value vocabulary (index 0 = highest priority). Collected on the apply
// form (Service Listing step) and editable in the pro dashboard; stored as
// `client_types text[]` on `professional_applications` + `professionals`.
//
// NOT shown on the public profile — this is internal signal for better
// client↔pro matching later (mirrors the forward-looking `country` field on
// client_preferences). Admin can review it on the applicant preview.
//
// Stable slugs are the stored/compared values (DB CHECK-constrained); labels
// are display-only and localize like other data labels (EN authoritative, VI
// machine-draft pending native review), NOT via the message JSON.

export const CLIENT_TYPES = [
  "individuals",
  "startups",
  "smes",
  "enterprises",
] as const;

export type ClientType = (typeof CLIENT_TYPES)[number];

const CLIENT_TYPE_SET: ReadonlySet<string> = new Set(CLIENT_TYPES);

export function isClientType(value: unknown): value is ClientType {
  return typeof value === "string" && CLIENT_TYPE_SET.has(value);
}

// Sanitize an arbitrary value into a valid ordered client-type list: keep only
// known slugs, drop duplicates (first occurrence wins — preserves rank order),
// cap at the full set. Used at every trust boundary (apply payload, dashboard
// save) so a hand-crafted POST can never store a bogus/duplicated ranking.
export function toClientTypes(value: unknown): ClientType[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: ClientType[] = [];
  for (const item of value) {
    if (isClientType(item) && !seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

// Was a hand-copied union; derives from `routing.locales` now.

const LABELS_EN: Record<ClientType, string> = {
  individuals: "Individuals",
  startups: "Startups",
  smes: "Small & medium businesses",
  enterprises: "Large enterprises",
};

// VI machine-draft — pending native review (consistent with the rest of VI).
const LABELS_FR: Record<ClientType, string> = {
  individuals: "Particuliers",
  startups: "Startups",
  smes: "PME",
  enterprises: "Grandes entreprises",
};

const LABELS_VI: Record<ClientType, string> = {
  individuals: "Cá nhân",
  startups: "Công ty khởi nghiệp",
  smes: "Doanh nghiệp vừa và nhỏ",
  enterprises: "Doanh nghiệp lớn",
};

const LABELS_ZH_HANS: Record<ClientType, string> = {
  individuals: "个人",
  startups: "初创企业",
  smes: "中小企业",
  enterprises: "大型企业",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const LABELS_ZH_HANT: Record<ClientType, string> = {
  individuals: "個人",
  startups: "初創企業",
  smes: "中小企業",
  enterprises: "大型企業",
};

// EN-fallback getter; a locale with no map falls back to EN.
export function getClientTypeLabel(slug: ClientType, locale: Locale = "en"): string {
  return localeLabel(locale, slug, LABELS_EN[slug], {
    vi: LABELS_VI,
    fr: LABELS_FR,
    "zh-Hans": LABELS_ZH_HANS,
    "zh-Hant": LABELS_ZH_HANT,
  });
}
